import React, { useEffect, useState } from 'react';
import { PartyFundingDoc } from '../types/types';
import { getPartyFunding } from '../data/cache';
import Layout from '../components/Layout';
import PageContainer from '../layout/PageContainer';
import BackLink from '../components/BackLink';
import PageHeader from '../components/PageHeader';
import { Card, CardHeader } from '../components/Card';
import Icon from '../components/Icon';
import { color, font, radius, space, text, weight } from '../theme';

/* "איך מפלגות ממומנות" (owner, 2026-10-17: its own page, linked from every party's מימון
   tab). Plain-language rules of חוק מימון מפלגות, with the current amounts from
   meta/partyFunding when it loads; the text reads the same without them. */

// Desktop reading column; the breadcrumb row above it (Layout width) uses the same width
const READING_WIDTH = 800;

const paragraph: React.CSSProperties = { ...text.body, margin: 0, color: color.textSecondary, lineHeight: 1.7 };
const list: React.CSSProperties = { margin: 0, paddingInlineStart: space.xl, fontSize: font.body, color: color.textSecondary, lineHeight: 1.8 };

const AmountRow = ({ label, value }: { label: string; value: string }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', gap: space.sm, padding: `${space.sm + 2}px ${space.md}px`, background: color.surfaceMuted, borderRadius: radius.md }}>
    <span style={{ fontSize: font.label, color: color.text }}>{label}</span>
    <span style={{ fontSize: font.label, fontWeight: weight.bold, color: color.text, whiteSpace: 'nowrap' }}>{value}</span>
  </div>
);

const ExternalLink = ({ href, children }: { href: string; children: React.ReactNode }) => (
  <a href={href} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: space.xs, fontSize: font.label, fontWeight: weight.medium, color: color.primary, textDecoration: 'none' }}>
    <Icon name="external" size={16} />{children}
  </a>
);

const shekels = (n: number) => `${Math.round(n).toLocaleString('he-IL')} ₪`;

const FundingExplainerPage = () => {
  const [data, setData] = useState<PartyFundingDoc | null>(null);

  useEffect(() => {
    let cancelled = false;
    // The amounts are a bonus: without the doc the page still explains the rules
    getPartyFunding().then(d => { if (!cancelled) setData(d); }).catch(() => { /* ok */ });
    return () => { cancelled = true; };
  }, []);

  const perSeatMonthly = data && data.unit ? Math.round(data.unit * data.monthlyShare) : null;
  const election = data ? data.election[String(data.campaign)] : undefined;

  return (
    <Layout width={READING_WIDTH} breadcrumbs={[{ label: 'איך מפלגות ממומנות' }]}>
      <PageContainer maxWidth={READING_WIDTH}>
        <div style={{ padding: space.lg, display: 'flex', flexDirection: 'column', gap: space.lg }}>
          <div><BackLink fallback="/parties" fallbackLabel="הכנסת" /></div>
          <PageHeader
            title="איך מפלגות ממומנות"
            subtitle="רוב הכסף של המפלגות בישראל בא מהמדינה, לפי נוסחה שקבועה בחוק מימון מפלגות. הכסף הפרטי קטן בהרבה, והחוק מגביל אותו."
          />

          <Card>
            <CardHeader title="1. המדינה משלמת לפי מנדטים" />
            <div style={{ display: 'flex', flexDirection: 'column', gap: space.md }}>
              <p style={paragraph}>החוק קובע "יחידת מימון", שאת גובהה קובעת ועדה ציבורית בראשות שופט. לפיה כל סיעה בכנסת מקבלת כסף בשני מסלולים: בכל חודש, להוצאות השוטפות, ולקמפיין הבחירות.</p>
              {perSeatMonthly !== null && <AmountRow label="בכל חודש, לכל מנדט" value={shekels(perSeatMonthly)} />}
              {election && <AmountRow label={`לקמפיין לכנסת ה-${data!.campaign}, לכל מנדט`} value={shekels(election.unit)} />}
              <p style={{ ...paragraph, fontSize: font.small }}>
                המימון החודשי ניתן גם על הסיעה עצמה, כאילו הייתה עוד מנדט. לקמפיין, סיעה ותיקה מקבלת לפי ממוצע המנדטים שלה בכנסת היוצאת ובכנסת הנכנסת, וסיעה חדשה לפי המנדטים שזכתה בהם. כל רשימה שנבחרה מקבלת עוד {election ? `${election.extraUnits} יחידות` : 'יחידה'}{election && election.extraUnits !== 1 ? ` (הוראת שעה לבחירות לכנסת ה-${data!.campaign})` : ''}.
              </p>
            </div>
          </Card>

          <Card>
            <CardHeader title="2. תרומות: מעט, ובהגבלות" />
            <ul style={list}>
              <li>רק בעלי זכות בחירה יכולים לתרום. לא חברות, לא בעילום שם ולא במזומן.</li>
              <li>לכל תורם ובני ביתו יש תקרה של כמה אלפי שקלים בשנה.</li>
              <li>מפלגה שאינה מיוצגת בכנסת יכולה לקבל מכל תורם פי חמישה.</li>
              <li>המפלגות מדווחות על התרומות למבקר המדינה, והוא מפרסם אותן.</li>
              <li>בפועל זה מעט: בדוחות הכספיים של 2024 דיווחו כל הסיעות יחד על 2.6 מיליון ₪ בתרומות, כ-2% מהכנסותיהן. 88% (147.6 מיליון ₪) היו מימון ממלכתי.</li>
            </ul>
          </Card>

          <Card>
            <CardHeader title="3. ערבויות: שם הכסף הפרטי הגדול" />
            <p style={paragraph}>מפלגה חדשה עוד לא קיבלה מימון מהמדינה. כדי לנהל קמפיין היא לוקחת מקדמה מהמדינה או הלוואה מהבנק, ואנשים פרטיים ערבים לה. ערב לא נותן כסף, אבל מתחייב לשלם אם המפלגה לא תחזיר את חובה. ערבות אחת יכולה להגיע למיליוני שקלים.</p>
          </Card>

          <Card>
            <CardHeader title="4. חובות" />
            <p style={paragraph}>מבקר המדינה בודק כל שנה את החשבונות של כל סיעה: גירעון, הלוואות, ומימון שנשלל בגלל ליקויים. הדוחות מתפרסמים כשנתיים אחרי (הדוח על 2024 פורסם ב-2026), ולכן נתוני החובות הם תמיד של שנים קודמות.</p>
          </Card>

          <Card>
            <CardHeader title="מה המספרים באתר אומרים, ומה לא" />
            <div style={{ display: 'flex', flexDirection: 'column', gap: space.md }}>
              <ul style={list}>
                <li>המימון הממלכתי מחושב לפי הנוסחה בחוק. בפועל מנכים ממנו החזרי הלוואות וקנסות.</li>
                <li>תרומות, ערבויות והלוואות מוצגות כפי שהמפלגות דיווחו למבקר המדינה, בלי עיבוד שלו.</li>
                <li>"לא דווחו" לא אומר בהכרח שלא היה כסף, אלא שלא דווח.</li>
                <li>המאגר של מבקר המדינה כולל רק את מה שהמפלגות העלו אליו. לשנת 2024, למשל, הוא הראה באוקטובר 2026 כ-1.1 מיליון ₪ בתרומות לסיעות, מתוך 2.6 מיליון ₪ שהן דיווחו בדוחות הכספיים.</li>
                <li>"בקמפיין" הוא כל מה שדווח מאז הבחירות הקודמות, כולל תרומות שאחרי הבחירות לכיסוי חובות הקמפיין הקודם.</li>
                <li>שמות התורמים לא מוצגים באתר. ערבים ומלווים מוצגים בשמם, כפי שפרסם מבקר המדינה.</li>
              </ul>
              <div style={{ display: 'flex', flexDirection: 'column', gap: space.sm }}>
                <ExternalLink href="https://www.mevaker.gov.il/state-audit/elections/donations">הנתונים באתר מבקר המדינה</ExternalLink>
                <ExternalLink href="https://www.mevaker.gov.il/state-audit/elections/laws">חוק מימון מפלגות והנחיות המבקר</ExternalLink>
              </div>
            </div>
          </Card>
        </div>
      </PageContainer>
    </Layout>
  );
};

export default FundingExplainerPage;
